import { Link } from "react-router-dom";
import { ArrowRight, Cpu, Network, Sparkles, ShieldCheck, MapPin, Award, Headphones, Building2 } from "lucide-react";
import { IMG, SITE, BRANDS } from "@/lib/site";
import { Reveal } from "@/components/Reveal";
import { StatCounter } from "@/components/StatCounter";
import { SectionHeader } from "@/components/SectionHeader";
import { BrandWall } from "@/components/BrandWall";

const VERTICALS = [
  {
    title: "Fibaro India",
    eyebrow: "Vertical I",
    body: "Premium European Home Automation Distribution. Lighting, security, climate, energy and intelligent living solutions powered by Fibaro and Nice Group, Italy.",
    cta: { label: "Explore Fibaro", to: "/fibaro" },
    image: IMG.smartPanel,
    icon: Cpu,
  },
  {
    title: "Dealer Network",
    eyebrow: "Vertical II",
    body: "Building India's premium network of Fibaro partners through technical support, training and business development.",
    cta: { label: "Become a Partner", to: "/dealers" },
    image: IMG.villa,
    icon: Network,
  },
  {
    title: "Premium Interior Solutions",
    eyebrow: "Vertical III",
    body: "Curated materials and hardware solutions for sophisticated living spaces and discerning architects.",
    cta: { label: "Explore Solutions", to: "/interior" },
    image: IMG.hardware,
    icon: Sparkles,
  },
];

const TRUST = [
  { icon: Award, label: "Authorized Fibaro Distributor for India" },
  { icon: MapPin, label: "Pan-India Dealer Network" },
  { icon: Sparkles, label: "Premium Interior Brands" },
  { icon: Headphones, label: "Technical Project Support" },
  { icon: Building2, label: "Premium Experience Centre" },
];

export default function Home() {
  return (
    <>
      {/* HERO */}
      <section className="relative min-h-screen flex items-end overflow-hidden bg-[#F5EFE4]" data-testid="hero-section">
        {/* Subtle architectural photo texture — low opacity for depth */}
        <div className="absolute inset-0 opacity-20 mix-blend-multiply">
          <img src={IMG.hero} alt="" className="h-full w-full object-cover scale-105 animate-fade-in" loading="eager" />
        </div>
        {/* Cream gradient overlay — top-left brightest for logo clarity */}
        <div className="absolute inset-0 bg-gradient-to-br from-[#FAF6EC] via-[#F2ECDB]/70 to-[#E5D7B8]/40" />
        {/* Grain texture */}
        <div className="absolute inset-0 grain-overlay opacity-30" />
        {/* Subtle bottom fade for section transition */}
        <div className="absolute bottom-0 inset-x-0 h-24 bg-gradient-to-t from-background to-transparent" />

        <div className="relative container-edge pb-24 pt-32 text-charcoal-900">
          <p className="eyebrow text-charcoal-700 mb-6 flex items-center gap-3" data-testid="hero-eyebrow">
            <span className="divider-gold" /> India · Coimbatore
          </p>

          {/* Logo masthead — transparent logos on light cream backdrop */}
          <div
            className="mb-14 inline-block"
            data-testid="hero-logo-masthead"
          >
            <div className="flex flex-col sm:flex-row items-start sm:items-center gap-6 sm:gap-12">
              <img
                src="/brands/jis-logo-transparent.png"
                alt="Jain Interior Solutions"
                className="h-16 sm:h-20 md:h-24 w-auto block"
                data-testid="hero-jis-logo"
                draggable={false}
              />
              <span className="hidden sm:block h-16 w-px bg-charcoal-900/20" />
              <div className="flex flex-col items-start gap-3">
                <span className="font-mono text-[10px] uppercase tracking-[0.28em] text-charcoal-500">
                  Authorized Distributor
                </span>
                <img
                  src={IMG.fibaroLogo}
                  alt="Fibaro — a Nice brand"
                  className="h-10 sm:h-12 md:h-14 w-auto block"
                  data-testid="hero-fibaro-logo"
                  draggable={false}
                />
              </div>
            </div>
          </div>

          <h1 className="display-1 max-w-5xl text-charcoal-900" data-testid="hero-headline">
            Intelligent Living.<br />
            Premium <span className="italic font-normal text-gold">Interiors</span>.<br />
            Pan-India Distribution.
          </h1>
          <p className="mt-10 text-base md:text-lg max-w-2xl text-charcoal-600 leading-relaxed" data-testid="hero-sub">
            Jain Interior Solutions is the authorized distributor of Fibaro Home Automation in India,
            delivering world-class smart home solutions, dealer partnerships and carefully curated
            premium interior materials and hardware solutions that redefine modern living.
          </p>
          <div className="mt-12 flex flex-wrap gap-4" data-testid="hero-ctas">
            <Link to="/fibaro" data-testid="cta-explore-fibaro" className="btn-gold">
              Explore Fibaro <ArrowRight className="h-4 w-4" />
            </Link>
            <Link to="/dealers" data-testid="cta-become-dealer" className="btn-ghost text-charcoal-900 border-charcoal-900/40">
              Become a Dealer
            </Link>
            <Link to="/contact" data-testid="cta-experience-centre" className="btn-ghost text-charcoal-900 border-charcoal-900/40">
              Visit Our Experience Centre
            </Link>
          </div>

          <div className="mt-20 flex items-center gap-6 text-xs uppercase tracking-[0.22em] text-charcoal-500">
            <span className="h-px w-12 bg-charcoal-400" />
            <span>Scroll to explore</span>
          </div>
        </div>
      </section>

      {/* THREE VERTICALS */}
      <section className="py-24 md:py-32 bg-background" data-testid="verticals-section">
        <div className="container-edge">
          <Reveal>
            <SectionHeader
              eyebrow="One Company · Three Verticals"
              title={<>A single destination for <span className="italic text-gold">intelligent living</span> and curated interiors.</>}
            />
          </Reveal>
          <div className="mt-20 grid lg:grid-cols-3 gap-10 lg:gap-8">
            {VERTICALS.map((v, i) => (
              <Reveal key={v.title} delay={i * 120}>
                <article className="group relative card-luxe" data-testid={`vertical-card-${i}`}>
                  <div className="aspect-[4/5] overflow-hidden">
                    <img
                      src={v.image}
                      alt={v.title}
                      loading="lazy"
                      className="h-full w-full object-cover transition-transform duration-[1400ms] group-hover:scale-105 grayscale-[20%] group-hover:grayscale-0"
                    />
                  </div>
                  <div className="p-8 md:p-10">
                    <p className="eyebrow flex items-center gap-3">
                      <v.icon className="h-3.5 w-3.5" /> {v.eyebrow}
                    </p>
                    <h3 className="display-3 mt-5">{v.title}</h3>
                    <p className="mt-5 text-sm leading-relaxed text-muted-foreground">{v.body}</p>
                    <Link
                      to={v.cta.to}
                      data-testid={`vertical-cta-${i}`}
                      className="mt-8 inline-flex items-center gap-3 text-xs uppercase tracking-[0.22em] font-medium text-gold hover:gap-5 transition-all"
                    >
                      {v.cta.label} <ArrowRight className="h-4 w-4" />
                    </Link>
                  </div>
                </article>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* TRUST BAR */}
      <section className="border-y border-border bg-secondary/40" data-testid="trust-bar">
        <div className="container-edge py-10">
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-6 lg:gap-4">
            {TRUST.map(({ icon: Icon, label }, i) => (
              <div key={i} className="flex items-center gap-3" data-testid={`trust-item-${i}`}>
                <Icon className="h-5 w-5 text-gold shrink-0" />
                <span className="text-[11px] uppercase tracking-[0.18em] leading-snug text-foreground/80">{label}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ABOUT GLIMPSE */}
      <section className="py-24 md:py-32" data-testid="about-glimpse">
        <div className="container-edge grid lg:grid-cols-12 gap-12 lg:gap-20 items-center">
          <Reveal className="lg:col-span-5">
            <div className="relative aspect-[4/5] overflow-hidden">
              <img src={IMG.about} alt="Premium interior" loading="lazy" className="h-full w-full object-cover" />
              <div className="absolute inset-0 ring-1 ring-inset ring-gold/30" />
            </div>
          </Reveal>
          <Reveal delay={150} className="lg:col-span-7">
            <p className="eyebrow flex items-center gap-3"><span className="divider-gold" /> About Us</p>
            <h2 className="display-2 mt-5">
              Creating <span className="italic text-gold">intelligent spaces</span> for modern living.
            </h2>
            <p className="mt-8 text-base md:text-lg text-muted-foreground leading-relaxed">
              We combine world-class home automation technologies with carefully selected interior materials
              and hardware solutions to create sophisticated living experiences. As the authorized distributor
              of Fibaro in India, we support homeowners, architects, designers, builders and system integrators
              across the country.
            </p>
            <Link to="/about" data-testid="about-link" className="link-underline mt-10 text-gold">
              Read Our Story <ArrowRight className="h-4 w-4" />
            </Link>
          </Reveal>
        </div>
      </section>

      {/* STATS */}
      <section className="py-24 md:py-32 bg-charcoal-900 text-white relative overflow-hidden" data-testid="stats-section">
        <div className="absolute inset-0 grain-overlay opacity-30" />
        <div className="container-edge relative">
          <Reveal>
            <SectionHeader
              eyebrow="By the Numbers"
              title={<span className="text-white">A national footprint built on <span className="italic text-gold">trust</span>.</span>}
            />
          </Reveal>
          <div className="mt-20 grid grid-cols-2 md:grid-cols-4 gap-10 md:gap-16">
            <StatCounter value={42} suffix="+" label="Cities Served" />
            <StatCounter value={65} suffix="+" label="Dealer Partners" />
            <StatCounter value={280} suffix="+" label="Projects Supported" />
            <StatCounter value={18} suffix="+" label="Years of Experience" />
          </div>
        </div>
      </section>

      {/* BRANDS */}
      <BrandWall />

      {/* CTA */}
      <section className="relative py-32 overflow-hidden" data-testid="cta-section">
        <div className="absolute inset-0">
          <img src={IMG.ctaBg} alt="" className="h-full w-full object-cover" loading="lazy" />
          <div className="absolute inset-0 bg-charcoal-900/80" />
        </div>
        <div className="container-edge relative text-white text-center">
          <Reveal>
            <p className="eyebrow text-white/70">Let's Build Together</p>
            <h2 className="display-1 mt-6 max-w-4xl mx-auto">
              Let's build <span className="italic text-gold">intelligent spaces</span> together.
            </h2>
            <p className="mt-8 text-white/70 max-w-2xl mx-auto leading-relaxed">
              Whether you're a homeowner, architect, interior designer, builder or system integrator,
              our team is ready to help you create exceptional intelligent living experiences.
            </p>
            <div className="mt-12 flex flex-wrap gap-4 justify-center">
              <Link to="/contact" data-testid="cta-speak" className="btn-gold">Speak With Us <ArrowRight className="h-4 w-4" /></Link>
              <Link to="/dealers" data-testid="cta-dealer" className="btn-ghost text-white border-white/60">Become a Dealer</Link>
              <Link to="/support" data-testid="cta-demo" className="btn-ghost text-white border-white/60">Request a Demo</Link>
            </div>
          </Reveal>
        </div>
      </section>
    </>
  );
}
